<template>
  <v-main>
    <v-container fluid>
      <v-toolbar class="elevation-0">
        <v-toolbar-title class="pl-0">FAQ</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="abrirNovo">
          <v-icon left>mdi-plus-circle-outline</v-icon> Nova pergunta
        </v-btn>
      </v-toolbar>

      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <div v-else-if="!items.length" class="text-center grey--text py-12">
        Nenhum item de FAQ. Clique em "Nova pergunta" para começar.
      </div>

      <template v-else>
        <v-card
          v-for="grupo in grupos"
          :key="grupo.category"
          class="mb-6 elevation-1"
        >
          <v-card-title class="subtitle-1 text-uppercase grey--text text--darken-1">
            {{ grupo.category }}
            <v-chip x-small class="ml-2" outlined>{{ grupo.items.length }}</v-chip>
          </v-card-title>
          <v-divider></v-divider>
          <v-data-table
            :headers="headers"
            :items="grupo.items"
            hide-default-footer
            disable-pagination
            disable-sort
          >
            <template v-slot:item.order="{ item }">
              <v-chip small label outlined>{{ item.order }}</v-chip>
            </template>
            <template v-slot:item.question="{ item }">
              <div class="font-weight-medium">{{ item.question }}</div>
              <div class="caption grey--text text-truncate" style="max-width: 480px;">{{ item.answer }}</div>
            </template>
            <template v-slot:item.active="{ item }">
              <v-switch
                :input-value="item.active"
                :loading="togglingId === item.id"
                color="success"
                hide-details
                dense
                class="mt-0 pt-0"
                @change="toggleActive(item)"
              ></v-switch>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn color="primary" fab text small @click="abrirEdicao(item)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn color="error" fab text small @click="confirmarExclusao(item)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </template>
    </v-container>

    <!-- Form criar/editar -->
    <v-dialog v-model="dialogForm" max-width="640" persistent scrollable>
      <v-card>
        <v-card-title class="subtitle-1">
          {{ form.id ? 'Editar pergunta' : 'Nova pergunta' }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-form ref="form">
            <v-textarea
              v-model="form.question"
              label="Pergunta *"
              rows="2"
              auto-grow
              :rules="[v => !!(v && v.trim()) || 'Informe a pergunta']"
              outlined
            ></v-textarea>
            <v-textarea
              v-model="form.answer"
              label="Resposta *"
              rows="4"
              auto-grow
              hint="Suporta múltiplas linhas (quebras de linha são preservadas)"
              persistent-hint
              :rules="[v => !!(v && v.trim()) || 'Informe a resposta']"
              outlined
            ></v-textarea>
            <v-combobox
              v-model="form.category"
              :items="categorias"
              label="Categoria *"
              hint="Selecione uma existente ou digite para criar uma nova"
              persistent-hint
              :rules="[v => !!(v && v.trim()) || 'Informe a categoria']"
              outlined
              @change="onCategoryChange"
            ></v-combobox>
            <div class="d-flex align-center">
              <v-text-field
                v-model.number="form.order"
                label="Ordem"
                type="number"
                min="0"
                hint="Ordem de exibição dentro da categoria (crescente)"
                persistent-hint
                outlined
                class="mr-4"
                style="max-width: 200px;"
              ></v-text-field>
              <v-switch
                v-model="form.active"
                :label="form.active ? 'Ativo (visível no app)' : 'Inativo'"
                color="success"
              ></v-switch>
            </div>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogForm = false" :disabled="saving">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="salvar">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmação de exclusão -->
    <v-dialog v-model="dialogDelete" max-width="420">
      <v-card>
        <v-card-title class="subtitle-1">Excluir pergunta</v-card-title>
        <v-card-text>
          Tem certeza que deseja excluir
          <strong>"{{ selecionada && selecionada.question }}"</strong>? Esta ação não pode ser desfeita.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogDelete = false" :disabled="deleting">Cancelar</v-btn>
          <v-btn color="error" :loading="deleting" @click="excluir">Excluir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import { mapMutations } from 'vuex'
import { listFaq, createFaq, updateFaq, setFaqActive, removeFaq } from './faqStorage'

const emptyForm = () => ({
  id: null,
  question: '',
  answer: '',
  category: '',
  order: 0,
  active: true
})

export default {
  name: 'FaqList',
  data: () => ({
    loading: false,
    saving: false,
    deleting: false,
    togglingId: null,
    dialogForm: false,
    dialogDelete: false,
    selecionada: null,
    items: [],
    form: emptyForm(),
    headers: [
      { text: 'Ordem', value: 'order', width: 90 },
      { text: 'Pergunta', value: 'question' },
      { text: 'Ativo', value: 'active', width: 90, align: 'center' },
      { text: '', value: 'actions', sortable: false, align: 'end', width: 120 }
    ]
  }),
  computed: {
    // Categorias existentes (únicas) para o dropdown do form.
    categorias () {
      return [...new Set(this.items.map(i => i.category).filter(Boolean))].sort()
    },
    // Itens agrupados por categoria, cada grupo ordenado por `order` asc.
    grupos () {
      const map = {}
      this.items.forEach(item => {
        const cat = item.category || '(sem categoria)'
        if (!map[cat]) map[cat] = []
        map[cat].push(item)
      })
      return Object.keys(map).sort().map(category => ({
        category,
        items: map[category].slice().sort((a, b) => (a.order || 0) - (b.order || 0))
      }))
    }
  },
  methods: {
    ...mapMutations(['SET_SNACKBAR']),
    getData () {
      this.loading = true
      listFaq()
        .then(items => {
          this.items = items
          this.loading = false
        })
        .catch(() => {
          this.SET_SNACKBAR({ text: 'Houve um erro ao carregar o FAQ.' })
          this.loading = false
        })
    },
    abrirNovo () {
      this.form = emptyForm()
      if (this.$refs.form) this.$refs.form.resetValidation()
      this.dialogForm = true
    },
    abrirEdicao (item) {
      this.form = { ...item }
      if (this.$refs.form) this.$refs.form.resetValidation()
      this.dialogForm = true
    },
    // Ao escolher/criar a categoria num item novo, sugere a próxima ordem.
    onCategoryChange () {
      if (this.form.id) return
      this.form.order = this.proximaOrdem(this.form.category)
    },
    proximaOrdem (category) {
      const naCategoria = this.items.filter(i => i.category === category)
      if (!naCategoria.length) return 0
      return Math.max(...naCategoria.map(i => i.order || 0)) + 1
    },
    salvar () {
      if (!this.$refs.form.validate()) return
      this.saving = true
      const data = {
        question: this.form.question,
        answer: this.form.answer,
        category: this.form.category,
        order: this.form.order,
        active: this.form.active
      }
      const action = this.form.id
        ? updateFaq(this.form.id, data)
        : createFaq(data)
      action
        .then(() => {
          this.SET_SNACKBAR({ text: this.form.id ? 'Pergunta atualizada' : 'Pergunta criada' })
          this.dialogForm = false
          this.saving = false
          this.getData()
        })
        .catch(() => {
          this.SET_SNACKBAR({ text: 'Houve um erro ao salvar a pergunta.' })
          this.saving = false
        })
    },
    toggleActive (item) {
      const novoValor = !item.active
      this.togglingId = item.id
      setFaqActive(item.id, novoValor)
        .then(() => {
          item.active = novoValor
          this.togglingId = null
        })
        .catch(() => {
          this.SET_SNACKBAR({ text: 'Não foi possível alterar o status.' })
          this.togglingId = null
        })
    },
    confirmarExclusao (item) {
      this.selecionada = item
      this.dialogDelete = true
    },
    excluir () {
      this.deleting = true
      removeFaq(this.selecionada.id)
        .then(() => {
          this.SET_SNACKBAR({ text: 'Pergunta excluída' })
          this.items = this.items.filter(i => i.id !== this.selecionada.id)
          this.dialogDelete = false
          this.deleting = false
        })
        .catch(() => {
          this.SET_SNACKBAR({ text: 'Houve um erro ao excluir a pergunta.' })
          this.deleting = false
        })
    }
  },
  created () {
    this.getData()
  }
}
</script>
